import { useSyncExternalStore } from "react"
import { useQueryClient, type QueryClient } from "@tanstack/react-query"
import { SYNC_JOB_STALL_MS } from "@nova-ai/shared"

import {
  getOrdersSyncJob,
  getScheduledOrdersSyncRun,
  startOrdersSyncJob,
  type DetectSummary,
  type EnrichSummary,
  type MarketplaceSyncSummary,
  type OrdersSyncJobSnapshot,
  type OrdersSyncStageStatus,
} from "./use-orders"

export type StepSummary =
  | { kind: "enrich"; summary: EnrichSummary }
  | { kind: "marketplaces"; summary: MarketplaceSyncSummary }
  | { kind: "detect"; summary: DetectSummary }

export type StepStatus =
  | { kind: "pending" }
  | { kind: "running" }
  | { kind: "done"; result: StepSummary }
  | { kind: "error"; message: string }
  | { kind: "skipped" }

export type SyncProgress = {
  /** Completion time stays with this run across page navigation. */
  finishedAt?: string
  marketplaces: StepStatus
  baselinker: StepStatus
  magento: StepStatus
  oms: StepStatus
  detect: StepStatus
}

// Module-level store so the progress view outlives the Orders page. The Cloud
// Run execution is server-owned, so navigation never stops it. This store lives
// for the SPA session only: a full browser reload loses the current attachment,
// while the logical run and workload continue durably on the server.
let progress: SyncProgress | null = null
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) {
    listener()
  }
}

function setProgress(next: SyncProgress) {
  progress = next
  emit()
}

function clearProgress() {
  progress = null
  emit()
}

/** A finished run stays visible until the operator dismisses its summary. */
export function dismissOrderSync() {
  if (!isSyncing(progress)) {
    clearProgress()
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): SyncProgress | null {
  return progress
}

export function isSyncing(state: SyncProgress | null): boolean {
  if (!state) {
    return false
  }
  return (
    state.marketplaces.kind === "running" ||
    state.baselinker.kind === "running" ||
    state.magento.kind === "running" ||
    state.oms.kind === "running" ||
    state.detect.kind === "running"
  )
}

/** Subscribe a component to the latest sync progress (null until the first
 *  run). Backed by the module store, so every mounted consumer — the Orders
 *  panel, the global header indicator — stays in sync regardless of route. */
export function useOrderSyncProgress(): SyncProgress | null {
  return useSyncExternalStore(subscribe, getSnapshot)
}

/** Returns a stable callback that kicks off the sync pipeline. Safe to call
 *  from any page; a second call while a run is in flight is a no-op. */
export function useStartOrderSync(): () => void {
  const qc = useQueryClient()
  return () => void runOrderSync(qc)
}

const POLL_INTERVAL_MS = 2_000

// The stages the banner renders, in pipeline order — the full server pipeline.
const UI_STAGES = ["marketplaces", "baselinker", "magento", "oms", "detect"] as const

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

type FollowedOrdersSync =
  | { kind: "awaiting_workload" }
  | { kind: "terminal_error"; message: string }
  | { kind: "workload"; workload: OrdersSyncJobSnapshot }

async function followOrdersSync(
  runId: string,
  workload: OrdersSyncJobSnapshot | null
): Promise<FollowedOrdersSync> {
  if (workload) {
    const next = await getOrdersSyncJob(workload.id)
    return next && next.status !== "interrupted"
      ? { kind: "workload", workload: next }
      : { kind: "awaiting_workload" }
  }
  const logicalRun = await getScheduledOrdersSyncRun(runId)
  if (logicalRun.status === "failed" || logicalRun.status === "interrupted") {
    return {
      kind: "terminal_error",
      message: logicalRun.failureSummary ?? `Orders sync ${logicalRun.status}`,
    }
  }
  if (!logicalRun.workload) {
    return { kind: "awaiting_workload" }
  }
  const next = await getOrdersSyncJob(logicalRun.workload.id)
  return next && next.status !== "interrupted"
    ? { kind: "workload", workload: next }
    : { kind: "awaiting_workload" }
}

function startingProgress(): SyncProgress {
  return {
    marketplaces: { kind: "running" },
    baselinker: { kind: "pending" },
    magento: { kind: "pending" },
    oms: { kind: "pending" },
    detect: { kind: "pending" },
  }
}

async function runOrderSync(qc: QueryClient): Promise<void> {
  // Re-entrancy guard: ignore a click while a pipeline is already running.
  if (isSyncing(progress)) {
    return
  }
  const errMsg = (cause: unknown) => (cause instanceof Error ? cause.message : String(cause))
  const refreshOrders = () => void qc.invalidateQueries({ queryKey: ["orders"] })

  // The full refresh — Mirakl spine → Baselinker → Magento → OMS → Detect — runs
  // as a server-owned background job. We start it and poll: the work outlives
  // this request (and even this page; the module store keeps progress across
  // navigation), so we never hold a socket open for the minutes of rate-limited
  // Baselinker enrichment that used to time the request out and show a phantom
  // "Baselinker failed". Each poll reflects live per-stage progress and refills
  // the grid as stages land.
  setProgress(startingProgress())

  let runId: string
  let job: OrdersSyncJobSnapshot | null
  try {
    const started = await startOrdersSyncJob()
    runId = started.run.id
    job = started.workload
  } catch (cause) {
    setProgress(degradeOnError(progress, errMsg(cause)))
    return
  }
  if (job) {
    setProgress(toSyncProgress(job))
    refreshOrders()
  }

  // Track the server-side `updatedAt`, mirroring the CLI poller: as
  // long as the server keeps bumping it, the pipeline is alive — however long a
  // stage takes — so there is NO fixed wall-clock deadline (a wall-clock cap
  // falsely "timed out" honestly-long sweeps). Only an `updatedAt` frozen past the
  // stall window means the job is dead. That covers the restart gap boot
  // reconcile can't: a row orphaned moments before a boot isn't stale yet at
  // that boot's reconcile, so without this check the banner would poll a corpse
  // (and `isSyncing` would swallow re-clicks) until a later start/boot flips it.
  let lastUpdatedAt = job?.updatedAt ?? null
  let lastChangeAt = Date.now()
  while (job?.status !== "finished") {
    await delay(POLL_INTERVAL_MS)
    let followed: FollowedOrdersSync
    try {
      followed = await followOrdersSync(runId, job)
    } catch (cause) {
      setProgress(degradeOnError(progress, errMsg(cause)))
      return
    }
    if (followed.kind === "terminal_error") {
      setProgress(degradeOnError(progress, followed.message))
      return
    }
    if (followed.kind === "awaiting_workload") {
      job = null
      continue
    }
    const next = followed.workload
    if (next.updatedAt !== lastUpdatedAt) {
      lastUpdatedAt = next.updatedAt
      lastChangeAt = Date.now()
    }
    if (Date.now() - lastChangeAt > SYNC_JOB_STALL_MS) {
      // The job is gone, was interrupted, or its `updatedAt` froze: the API
      // restarted mid-sync (a dev hot-reload or a prod deploy) and boot
      // reconcile flipped the row — or hasn't yet — or its row was pruned. The
      // pipeline's writes are durable/idempotent, so refresh to show whatever
      // landed and clear the banner rather than cry "failed" on a stage that
      // didn't actually fail.
      refreshOrders()
      clearProgress()
      return
    }
    job = next
    setProgress(toSyncProgress(job))
    refreshOrders()
  }
}

/** Map a job snapshot's live per-stage status to the banner's SyncProgress.
 *  While the job runs, a not-yet-reached stage reads `skipped` server-side — show
 *  it as `pending` until the job finishes, when `skipped` is the final verdict
 *  (e.g. a hard upstream failure skipped it). */
function toSyncProgress(job: OrdersSyncJobSnapshot): SyncProgress {
  const finished = job.status === "finished"
  return {
    ...(finished ? { finishedAt: job.updatedAt } : {}),
    marketplaces: toStep("marketplaces", job.sync.marketplaces, finished),
    baselinker: toStep("baselinker", job.sync.baselinker, finished),
    magento: toStep("magento", job.sync.magento, finished),
    oms: toStep("oms", job.sync.oms, finished),
    detect: toStep("detect", job.sync.detect, finished),
  }
}

function toStep(
  key: (typeof UI_STAGES)[number],
  stage: OrdersSyncStageStatus,
  finished: boolean
): StepStatus {
  switch (stage.status) {
    case "running":
      return { kind: "running" }
    case "error":
      return { kind: "error", message: stage.message }
    case "skipped":
      return finished ? { kind: "skipped" } : { kind: "pending" }
    // `partial` (some marketplaces failed to sync) still produced a result; show
    // it as done — the per-marketplace failures ride in the job's `hints`.
    case "done":
    case "partial":
      return { kind: "done", result: toStepSummary(key, stage.result) }
  }
}

function toStepSummary(key: (typeof UI_STAGES)[number], result: unknown): StepSummary {
  if (key === "marketplaces") {
    return { kind: "marketplaces", summary: result as MarketplaceSyncSummary }
  }
  if (key === "detect") {
    return { kind: "detect", summary: result as DetectSummary }
  }
  return { kind: "enrich", summary: result as EnrichSummary }
}

/** Degrade the banner when the job can't be started or polled to completion:
 *  keep finished stages, flag the first unfinished stage with the error, and
 *  skip the rest. */
function degradeOnError(prev: SyncProgress | null, message: string): SyncProgress {
  const base = prev ?? startingProgress()
  const next = { ...base, finishedAt: new Date().toISOString() }
  let flagged = false
  for (const key of UI_STAGES) {
    if (base[key].kind === "done") {
      continue
    }
    next[key] = flagged ? { kind: "skipped" } : { kind: "error", message }
    flagged = true
  }
  return next
}
